import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEUR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ArrowLeftRight, Inbox, Send, Check, X as XIcon, Ban, CheckCircle2,
  Search, User, Calendar, MapPin, Phone, Users, Car, Link2, Clock, FileText, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const STATUS_META = {
  pending:   { label: "In attesa",  dot: "bg-amber-400",   chip: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  accepted:  { label: "Accettato",  dot: "bg-emerald-400", chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  rejected:  { label: "Rifiutato",  dot: "bg-rose-400",    chip: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  cancelled: { label: "Annullato",  dot: "bg-slate-400",   chip: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  completed: { label: "Completato", dot: "bg-sky-400",     chip: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
};
const STATUS_ALL = Object.keys(STATUS_META);
const fmtDate = (iso) => { if (!iso) return "-"; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

export default function ScambioServizi() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("inbox"); // inbox | sent | all
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [meOpId, setMeOpId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = tab === "all" ? {} : { box: tab };
      const [r, d, v, od, me] = await Promise.all([
        api.get("/exchanges", { params }),
        api.get("/drivers").catch(() => ({ data: [] })),
        api.get("/vehicles").catch(() => ({ data: [] })),
        api.get("/operators/directory").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: {} })),
      ]);
      setRows(r.data || []);
      setDrivers(d.data || []);
      setVehicles(v.data || []);
      setOperators(od.data || []);
      setMeOpId(me.data?.operator_id || null);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter !== "all") out = out.filter(r => r.status === statusFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(r =>
        (r.exchange_number || "").toLowerCase().includes(q) ||
        (r.passenger_name || "").toLowerCase().includes(q) ||
        (r.customer || "").toLowerCase().includes(q) ||
        (r.pickup || "").toLowerCase().includes(q) ||
        (r.destination || "").toLowerCase().includes(q) ||
        (r.from_operator_name || "").toLowerCase().includes(q) ||
        (r.to_operator_name || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [rows, query, statusFilter]);

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, rejected: 0, cancelled: 0, completed: 0 };
    rows.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [rows]);

  const isReceiver = (r) => meOpId && r.to_operator_id === meOpId;
  const isSender = (r) => meOpId && r.operator_id === meOpId;

  const doAccept = async (r) => { try { await api.post(`/exchanges/${r.id}/accept`); toast.success("Scambio accettato"); load(); setSelectedDetail(null); } catch (e) { toast.error(formatApiError(e)); } };
  const doReject = async () => { try { await api.post(`/exchanges/${rejectDialog.id}/reject`, { reject_reason: rejectReason || null }); toast.success("Scambio rifiutato"); setRejectDialog(null); setRejectReason(""); load(); setSelectedDetail(null); } catch (e) { toast.error(formatApiError(e)); } };
  const doCancel = async (r) => {
    const prompt = r.status === "accepted"
      ? `Il servizio è già stato accettato dal destinatario (${r.to_operator_name || "collega"}).\n\nVuoi comunque annullare l'affidamento?\nIl destinatario riceverà una notifica di annullamento.`
      : `Annullare lo scambio ${r.exchange_number}?\nIl servizio tornerà disponibile per gestione o nuova assegnazione.`;
    if (!confirm(prompt)) return;
    try { await api.post(`/exchanges/${r.id}/cancel`); toast.success(r.status === "accepted" ? "Affidamento annullato" : "Scambio annullato"); load(); setSelectedDetail(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doComplete = async (r) => { try { await api.post(`/exchanges/${r.id}/complete`); toast.success("Scambio completato"); load(); setSelectedDetail(null); } catch (e) { toast.error(formatApiError(e)); } };
  const assignDriver = async (r, driverId) => { try { await api.post(`/exchanges/${r.id}/assign-driver`, { to_driver_id: driverId || null, to_vehicle_id: r.to_vehicle_id || null }); toast.success("Autista aggiornato"); load(); if (selectedDetail?.id === r.id) setSelectedDetail({...selectedDetail, to_driver_id: driverId || null, to_driver_name: drivers.find(d=>d.id===driverId)?.first_name}); } catch (e) { toast.error(formatApiError(e)); } };
  const sendOperationalAssignment = async (r, driverId, vehicleId, force = false) => {
    try {
      const resp = await api.post(`/exchanges/${r.id}/assign-driver`, {
        to_driver_id: driverId || null,
        to_vehicle_id: vehicleId || null,
        force: !!force,
      });
      toast.success(force ? "Aggiornamento reinviato al collega" : "Aggiornamento inviato al collega");
      load();
      if (selectedDetail?.id === r.id) setSelectedDetail(resp.data || {...selectedDetail, to_driver_id: driverId, to_vehicle_id: vehicleId});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 pb-16" data-testid="scambio-page">
      {/* HEADER */}
      <div className="rounded-2xl border border-slate-800/80 bg-gradient-to-br from-slate-900 to-slate-950 p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/15 border border-purple-500/30"><ArrowLeftRight className="text-purple-400" size={20}/></div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Scambio Servizi</h1>
            </div>
            <div className="text-sm text-slate-400 mt-1.5 ml-12">Trasferisci servizi ad altri operatori NCC · nessun duplicato</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={busy} className="border-slate-800 bg-transparent text-slate-300"><RefreshCw size={14} className={busy ? "animate-spin" : ""}/> Aggiorna</Button>
          </div>
        </div>

        {/* STATUS SUMMARY */}
        <div className="mt-4 grid grid-cols-5 gap-2 text-center">
          {STATUS_ALL.map(k => {
            const M = STATUS_META[k];
            return (
              <div key={k} className={`rounded-xl border p-2.5 ${M.chip}`}>
                <div className="text-[10px] uppercase tracking-widest font-mono-num opacity-80">{M.label}</div>
                <div className="font-mono-num font-extrabold text-xl">{counts[k]}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* TABS + FILTERS */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900/50 p-1">
          <TabBtn active={tab==="inbox"} onClick={()=>setTab("inbox")} icon={Inbox} label="Ricevuti" count={rows.filter(r=>r.to_operator_id===meOpId).length} testid="tab-inbox"/>
          <TabBtn active={tab==="sent"} onClick={()=>setTab("sent")} icon={Send} label="Inviati" count={rows.filter(r=>r.operator_id===meOpId).length} testid="tab-sent"/>
          <TabBtn active={tab==="all"} onClick={()=>setTab("all")} icon={FileText} label="Storico" testid="tab-all"/>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
            <Input data-testid="ex-search" placeholder="Cerca..." value={query} onChange={e=>setQuery(e.target.value)} className="pl-9 bg-slate-900 border-slate-800 h-9 w-56"/>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="ex-status-filter" className="bg-slate-900 border-slate-800 h-9 w-40"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {STATUS_ALL.map(k => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* LIST */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="text-left p-3">N°</th>
                <th className="text-left">Data / Ora</th>
                <th className="text-left">{tab === "inbox" ? "Mittente" : tab === "sent" ? "Destinatario" : "Da → A"}</th>
                <th className="text-left">Cliente</th>
                <th className="text-left">Percorso</th>
                <th className="text-left">Driver</th>
                <th className="text-left">Stato</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-slate-500">
                  {tab === "inbox" ? "Nessuno scambio ricevuto" : tab === "sent" ? "Nessuno scambio inviato — apri una prenotazione e clicca ↔" : "Nessuno scambio"}
                </td></tr>
              )}
              {filtered.map(r => {
                const M = STATUS_META[r.status] || { label: r.status, chip: "bg-slate-500/15 text-slate-300 border-slate-500/40", dot: "bg-slate-400" };
                const iSender = isSender(r); const iReceiver = isReceiver(r);
                return (
                  <tr key={r.id} className="hover:bg-slate-900/40 cursor-pointer" onClick={()=>setSelectedDetail(r)} data-testid={`ex-row-${r.exchange_number}`}>
                    <td className="p-3 font-mono-num text-amber-400 text-xs whitespace-nowrap">{r.exchange_number || "-"}</td>
                    <td className="text-slate-300 whitespace-nowrap">
                      <div>{fmtDate(r.date)}</div>
                      <div className="text-xs text-slate-500 font-mono-num">{r.time || "-"}</div>
                    </td>
                    <td className="text-slate-300 max-w-[180px]">
                      {tab === "inbox" ? (
                        <div className="flex items-center gap-1.5"><Send size={12} className="text-purple-400"/> <span className="truncate">{r.from_operator_name || "-"}</span></div>
                      ) : tab === "sent" ? (
                        <div className="flex items-center gap-1.5"><Inbox size={12} className="text-sky-400"/> <span className="truncate">{r.to_operator_name || "-"}</span></div>
                      ) : (
                        <div className="text-xs">
                          <div className="text-slate-400 truncate">{r.from_operator_name || "?"} → <span className="text-slate-300">{r.to_operator_name || "?"}</span></div>
                        </div>
                      )}
                    </td>
                    <td className="text-slate-200 max-w-[160px] truncate">{r.customer || r.passenger_name || "-"}</td>
                    <td className="text-slate-400 text-xs max-w-[220px] truncate">{r.pickup} → {r.destination}</td>
                    <td className="text-slate-300 text-xs">{r.to_driver_name || (iSender ? <span className="text-slate-600">da assegnare</span> : "-")}</td>
                    <td><span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${M.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${M.dot}`}/>{M.label}</span></td>
                    <td className="pr-3 whitespace-nowrap" onClick={e=>e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {iReceiver && r.status === "pending" && (
                          <>
                            <button data-testid={`accept-${r.id}`} onClick={()=>doAccept(r)} className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-400" title="Accetta"><Check size={16}/></button>
                            <button data-testid={`reject-${r.id}`} onClick={()=>{setRejectDialog(r); setRejectReason("");}} className="p-1.5 rounded hover:bg-rose-500/20 text-rose-400" title="Rifiuta"><XIcon size={16}/></button>
                          </>
                        )}
                        {iSender && ["pending","accepted"].includes(r.status) && (
                          <button data-testid={`cancel-${r.id}`} onClick={()=>doCancel(r)}
                                  className={`p-1.5 rounded ${r.status==="accepted" ? "hover:bg-rose-500/20 text-rose-400" : "hover:bg-slate-800 text-slate-400"}`}
                                  title={r.status==="accepted" ? "Annulla Affidamento" : "Annulla Scambio"}><Ban size={14}/></button>
                        )}
                        {r.status === "accepted" && (iSender || iReceiver) && (
                          <button data-testid={`complete-${r.id}`} onClick={()=>doComplete(r)} className="p-1.5 rounded hover:bg-sky-500/20 text-sky-400" title="Completa"><CheckCircle2 size={16}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* DETAIL MODAL */}
      {selectedDetail && (
        <ExchangeDetailModal
          r={selectedDetail}
          isSender={isSender(selectedDetail)}
          isReceiver={isReceiver(selectedDetail)}
          drivers={drivers}
          onClose={()=>setSelectedDetail(null)}
          onAccept={()=>doAccept(selectedDetail)}
          onReject={()=>{ setRejectDialog(selectedDetail); setRejectReason(""); }}
          onCancel={()=>doCancel(selectedDetail)}
          onComplete={()=>doComplete(selectedDetail)}
          onAssignDriver={(id)=>assignDriver(selectedDetail, id)}
          onSendOperationalAssignment={(drvId, vehId, force)=>sendOperationalAssignment(selectedDetail, drvId, vehId, force)}
          vehicles={vehicles}
          nav={nav}
        />
      )}

      {/* REJECT DIALOG */}
      <Dialog open={!!rejectDialog} onOpenChange={(o)=>!o && setRejectDialog(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-white flex items-center gap-2"><XIcon className="text-rose-400" size={20}/> Rifiuta Scambio</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-400">Aggiungi una motivazione (opzionale) — il collega la vedrà nella sua notifica.</div>
          <Textarea data-testid="reject-reason" rows={3} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="Es. autista non disponibile su quella tratta" className="bg-slate-900 border-slate-800"/>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setRejectDialog(null)}>Annulla</Button>
            <Button data-testid="confirm-reject-btn" onClick={doReject} className="bg-rose-500 hover:bg-rose-400 text-white font-bold"><XIcon size={14}/> Conferma Rifiuto</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
        active ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-200"
      }`}>
      <Icon size={14}/> {label}
      {count > 0 && <span className={`text-[10px] font-mono-num px-1.5 py-0.5 rounded ${active ? "bg-slate-950/30" : "bg-slate-800/60"}`}>{count}</span>}
    </button>
  );
}

function ExchangeDetailModal({ r, isSender, isReceiver, drivers, vehicles, onClose, onAccept, onReject, onCancel, onComplete, onAssignDriver, onSendOperationalAssignment, nav }) {
  const M = STATUS_META[r.status] || { label: r.status, chip: "bg-slate-500/15 text-slate-300 border-slate-500/40", dot: "bg-slate-400" };
  return (
    <Dialog open onOpenChange={(o)=>!o && onClose()}>
      <DialogContent data-testid="ex-detail-modal" className="bg-slate-950 border-slate-800 text-slate-200 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white flex flex-wrap items-center gap-2">
            <ArrowLeftRight className="text-purple-400"/> Scambio {r.exchange_number}
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${M.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${M.dot}`}/>{M.label}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Operatori */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <PartyCard icon={Send} title="Mittente" name={r.from_operator_name} tone="purple"/>
          <PartyCard icon={Inbox} title="Destinatario" name={r.to_operator_name} tone="sky"/>
        </div>

        {/* Servizio */}
        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-3">Dettaglio Servizio</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Detail icon={Calendar} label="Data / Ora" value={`${fmtDate(r.date)} · ${r.time || "-"}`}/>
            <Detail icon={User} label="Cliente" value={r.customer || r.passenger_name || "-"}/>
            <Detail icon={Phone} label="Telefono" value={r.phone || "-"}/>
            <Detail icon={Users} label="Passeggeri" value={`${r.passengers || 1} · Bagagli ${r.luggage || 0}`}/>
            <Detail icon={Car} label="Servizio" value={r.service_type || "-"}/>
            <Detail
              label={r._price_is_compenso ? "Compenso fornitore" : "Prezzo"}
              value={<span className="text-amber-400 font-mono-num font-bold">{fmtEUR(r.price)}</span>}
            />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2">
            <Detail icon={MapPin} label="Partenza" value={r.pickup || "-"}/>
            <Detail icon={MapPin} label="Destinazione" value={r.destination || "-"}/>
            {r.stops && <Detail label="Fermate" value={r.stops}/>}
          </div>
        </div>

        {/* Note & Motivo rifiuto */}
        {r.notes && (
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Note</div>
            <div className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{r.notes}</div>
          </div>
        )}
        {r.status === "rejected" && r.reject_reason && (
          <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3">
            <div className="text-[10px] uppercase tracking-widest text-rose-400 font-mono-num">Motivo del rifiuto</div>
            <div className="text-sm text-rose-200 mt-1 whitespace-pre-wrap">{r.reject_reason}</div>
          </div>
        )}

        {/* Storico timeline */}
        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Storico</div>
          <div className="space-y-1.5 text-xs">
            <TimelineEvent icon={Send} label="Inviato" ts={r.created_at}/>
            {r.accepted_at && <TimelineEvent icon={Check} label="Accettato" ts={r.accepted_at} tone="emerald"/>}
            {r.rejected_at && <TimelineEvent icon={XIcon} label="Rifiutato" ts={r.rejected_at} tone="rose"/>}
            {r.cancelled_at && <TimelineEvent icon={Ban} label="Annullato" ts={r.cancelled_at} tone="slate"/>}
            {r.completed_at && <TimelineEvent icon={CheckCircle2} label="Completato" ts={r.completed_at} tone="sky"/>}
          </div>
        </div>

        {/* ASSEGNAZIONE OPERATIVA (iter 21) — solo destinatario, dopo accept.
            Driver + Veicolo (con targa auto) + pulsante "Invia aggiornamento al collega". */}
        {isReceiver && r.status === "accepted" && (
          <OperationalAssignmentPanel
            r={r}
            drivers={drivers}
            vehicles={vehicles}
            onSend={onSendOperationalAssignment}
          />
        )}

        {/* ASSEGNAZIONE OPERATIVA (vista MITTENTE, read-only) — compare quando il destinatario
            ha scelto almeno uno tra driver/veicolo. Mostra Driver / Veicolo / Targa aggiornati. */}
        {isSender && r.status !== "pending" && (r.to_driver_name || r.to_vehicle_name || r.to_vehicle_plate) && (
          <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="sender-view-assignment">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num mb-2">Assegnazione operativa comunicata dal collega</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Driver</div><div className="text-white font-bold mt-0.5">{r.to_driver_name || "—"}</div></div>
              <div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Veicolo</div><div className="text-white font-bold mt-0.5">{r.to_vehicle_name || "—"}</div></div>
              <div><div className="text-[10px] text-slate-500 uppercase tracking-widest">Targa</div><div className="text-amber-300 font-mono-num font-bold mt-0.5">{r.to_vehicle_plate || "—"}</div></div>
            </div>
            {r.assignment_updated_at && (
              <div className="text-[10px] text-slate-500 mt-2 font-mono-num">Aggiornato: {fmtDT(r.assignment_updated_at)}</div>
            )}
          </div>
        )}

        {/* Prenotazione collegata */}
        {r.booking_id && (
          <div className="mt-3">
            <button onClick={()=>nav("/prenotazioni")} className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300"><Link2 size={12}/> Prenotazione originale collegata</button>
          </div>
        )}

        {/* Actions footer */}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={onClose}>Chiudi</Button>
          {isReceiver && r.status === "pending" && (
            <>
              <Button data-testid="modal-reject-btn" onClick={onReject} className="bg-rose-500 hover:bg-rose-400 text-white font-bold"><XIcon size={14}/> Rifiuta</Button>
              <Button data-testid="modal-accept-btn" onClick={onAccept} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold"><Check size={14}/> Accetta Scambio</Button>
            </>
          )}
          {isSender && ["pending","accepted"].includes(r.status) && (
            <Button data-testid="modal-cancel-btn" onClick={onCancel}
                    className={r.status==="accepted"
                      ? "bg-rose-500 hover:bg-rose-400 text-white font-bold"
                      : "bg-transparent border border-slate-800 text-slate-300 hover:bg-slate-900"}>
              <Ban size={14}/> {r.status==="accepted" ? "Annulla Affidamento" : "Annulla Scambio"}
            </Button>
          )}
          {r.status === "accepted" && (isSender || isReceiver) && (
            <Button data-testid="modal-complete-btn" onClick={onComplete} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold"><CheckCircle2 size={14}/> Segna Completato</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** iter 21 — ASSEGNAZIONE OPERATIVA (lato destinatario, dopo accettazione).
 * Permette di scegliere Driver + Veicolo (targa auto-recuperata) e inviare l'aggiornamento al collega mittente.
 * Idempotente lato server: ripetere il submit con stessi dati non duplica né rinotifica. */
function OperationalAssignmentPanel({ r, drivers, vehicles, onSend }) {
  const [drvId, setDrvId] = React.useState(r.to_driver_id || "");
  const [vehId, setVehId] = React.useState(r.to_vehicle_id || "");
  React.useEffect(() => { setDrvId(r.to_driver_id || ""); setVehId(r.to_vehicle_id || ""); }, [r.id, r.to_driver_id, r.to_vehicle_id]);
  const selectedVehicle = vehicles.find(v => v.id === vehId);
  const plate = selectedVehicle?.plate || r.to_vehicle_plate || "";
  const dirty = (drvId || "") !== (r.to_driver_id || "") || (vehId || "") !== (r.to_vehicle_id || "");
  const canSend = !!(drvId || vehId);
  return (
    <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="operational-assignment-panel">
      <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono-num mb-3">Assegnazione operativa — Driver, Veicolo, Targa</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Driver</div>
          <Select value={drvId || "none"} onValueChange={(v)=>setDrvId(v==="none"?"":v)}>
            <SelectTrigger data-testid="assign-driver-select" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona autista"/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800 max-h-72">
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {drivers.filter(d=>d.active !== false).map(d=><SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Veicolo</div>
          <Select value={vehId || "none"} onValueChange={(v)=>setVehId(v==="none"?"":v)}>
            <SelectTrigger data-testid="assign-vehicle-select" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona veicolo"/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800 max-h-72">
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {vehicles.filter(v=>v.active !== false).map(v=><SelectItem key={v.id} value={v.id}>{v.brand} {v.model}{v.plate?` · ${v.plate}`:""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Targa (auto)</div>
          <div data-testid="assign-plate-view" className="h-10 flex items-center px-3 rounded-md border border-slate-800 bg-slate-950/60 text-amber-300 font-mono-num font-bold">{plate || "—"}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-slate-500 font-mono-num">
          {r.assignment_updated_at ? <>Ultimo invio: {fmtDT(r.assignment_updated_at)}</> : "Nessun aggiornamento ancora inviato"}
        </div>
        <div className="flex items-center gap-2">
          {r.assignment_updated_at && !dirty && (
            <Button
              data-testid="resend-operational-assignment-btn"
              disabled={!canSend}
              onClick={() => onSend(drvId, vehId, true)}
              variant="outline"
              className="border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10 font-bold"
            >
              <RefreshCw size={14}/> Reinvia aggiornamento
            </Button>
          )}
          <Button
            data-testid="send-operational-assignment-btn"
            disabled={!canSend}
            onClick={() => onSend(drvId, vehId, false)}
            className={`font-bold ${dirty || !r.assignment_updated_at ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950" : "bg-slate-800 hover:bg-slate-700 text-slate-200"}`}
          >
            <Send size={14}/> {r.assignment_updated_at && !dirty ? "Invio già effettuato" : (r.assignment_updated_at ? "Invia aggiornamento" : "Invia aggiornamento al collega")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PartyCard({ icon: Icon, title, name, tone }) {
  const tones = { purple: "border-purple-500/40 bg-purple-500/5 text-purple-300", sky: "border-sky-500/40 bg-sky-500/5 text-sky-300" };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-num opacity-80"><Icon size={11}/> {title}</div>
      <div className="text-white font-bold text-sm mt-1 truncate">{name || "—"}</div>
    </div>
  );
}
function Detail({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{Icon && <Icon size={10}/>} {label}</div>
      <div className="text-slate-200 mt-0.5 truncate">{value}</div>
    </div>
  );
}
function TimelineEvent({ icon: Icon, label, ts, tone }) {
  const t = { emerald: "text-emerald-400", rose: "text-rose-400", sky: "text-sky-400", slate: "text-slate-400" };
  return (
    <div className="flex items-center gap-2">
      <Icon size={12} className={t[tone] || "text-slate-500"}/>
      <span className="text-slate-300 font-bold">{label}</span>
      <span className="text-slate-500 font-mono-num ml-auto">{fmtDT(ts)}</span>
    </div>
  );
}
